import { useEffect, useRef } from 'react'

import { StoreBonusStatusResponse } from '@common/models/bonus'
import cx from 'classnames'

import { useCartContext } from '@/contexts/CartContext'
import {
  extractBonusValues,
  getDeliveredBonuses,
  getProgressPercentage,
  getBonusTrackCompletionStatus,
} from '@/features/storefronts/_storeBase/bonus/bonusUtils'
import { useGameSlug } from '@/features/storefronts/game-provider'
import { getTimeRemaining } from '@/utils/timer'

import { BonusTrackItem } from './BonusTrackItem'
import { BonusTrackCelebration } from './states/BonusTrackCelebration'
import { BonusTrackUnavailable } from './states/BonusTrackUnavailable'
import { TentativeBonus } from './TentativeBonus'
import { BonusTimeRemaining } from '../../shared/BonusTimeRemaining'

import styles from './bonusTrack.module.scss'

interface BonusTrackProps {
  bonusStatus: StoreBonusStatusResponse
  animationComplete?: boolean
}

export const BonusTrack = ({ bonusStatus, animationComplete }: BonusTrackProps) => {
  const gameSlug = useGameSlug()
  const { items } = useCartContext()

  const { balance, bonusItems, totalBalance } = extractBonusValues({ bonusStatus, items })
  const bonusTrackCompletionStatus = getBonusTrackCompletionStatus({ bonusStatus, items })
  const bonusesDelivered = getDeliveredBonuses({ bonusStatus, items })
  const progressPercentage = getProgressPercentage({ bonusStatus, items })
  const remainingTime = getTimeRemaining(bonusStatus?.endTimeMillis || 0)
  const showTentativeBonus = bonusesDelivered.length > 0

  const hasUnclaimedItems = bonusItems.filter((item) => item.available).some((item) => !item.claimed)
  const earnedItems = bonusItems.filter((item) => item.available || item.claimed)

  const upcomingItems = bonusItems.filter((item) => !item.available).filter((item) => !item.claimed)
  const upcomingTrackItems = showTentativeBonus
    ? upcomingItems.filter((item) => item.cost > totalBalance)
    : upcomingItems

  const exhaustivelyCompletedBonusTrack = bonusTrackCompletionStatus === 'EXHAUSTIVE'
  const bonusTrackUnavailable = bonusTrackCompletionStatus === 'UNAVAILABLE'

  const showRemainingTime = bonusStatus?.endTimeMillis && bonusStatus.endTimeMillis > 0

  const availableBonusRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (animationComplete) {
      if (earnedItems.length > 0 && hasUnclaimedItems) {
        const timeout = setTimeout(() => {
          if (availableBonusRef.current) {
            availableBonusRef.current.scrollIntoView({ behavior: 'smooth' })
          }
        }, 100)
        return () => clearTimeout(timeout)
      }

      if (showTentativeBonus) {
        const timeout = setTimeout(() => {
          document.getElementById('upcomingBonuses')?.scrollIntoView({ behavior: 'smooth' })
        }, 100)

        return () => clearTimeout(timeout)
      }

      if (exhaustivelyCompletedBonusTrack) {
        const timeout = setTimeout(() => {
          document.getElementById('bonusTrackCompleted')?.scrollIntoView({ behavior: 'smooth' })
        }, 100)

        return () => clearTimeout(timeout)
      }

      const timeout = setTimeout(() => {
        document.getElementById('unavailableBonuses')?.scrollIntoView({ behavior: 'smooth' })
      }, 100)

      return () => clearTimeout(timeout)
    }
  }, [animationComplete, earnedItems, hasUnclaimedItems, exhaustivelyCompletedBonusTrack, showTentativeBonus])

  return (
    <div className={styles.track}>
      <div className={cx(styles.earnedBonusContainer, styles[gameSlug])}>
        <div className={cx(styles.section, styles.earnedBonuses)} data-cy="available-bonuses">
          {(exhaustivelyCompletedBonusTrack ? bonusItems : earnedItems)?.map((item) => (
            <BonusTrackItem
              key={item.id}
              balance={balance}
              item={item}
              status={item.claimed ? 'claimed' : 'unclaimed'}
              progressPercentage={progressPercentage}
              ref={item.claimed ? null : availableBonusRef}
            />
          ))}
        </div>

        {exhaustivelyCompletedBonusTrack ? (
          <BonusTrackCelebration />
        ) : bonusTrackUnavailable ? (
          <BonusTrackUnavailable />
        ) : (
          <div className={cx(styles.upcomingBonusContainer, styles[gameSlug])} id="upcomingBonuses">
            {showTentativeBonus && (
              <div className={styles.tentativeBonusContainer}>
                <TentativeBonus bonusStatus={bonusStatus} />
              </div>
            )}

            <div
              className={cx(styles.section, styles.upcomingBonuses)}
              data-cy="unavailable-bonuses"
              id="unavailableBonuses"
            >
              {!!showRemainingTime && <BonusTimeRemaining remainingTime={remainingTime} gameSlug={gameSlug} />}

              {upcomingTrackItems?.map((item, index) => (
                <BonusTrackItem
                  key={item.id}
                  balance={totalBalance}
                  item={item}
                  status={index === 0 ? 'next' : 'upcoming'}
                  progressPercentage={progressPercentage}
                  lastItem={index === upcomingTrackItems.length - 1}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
